import { useInvariableEffect } from '@servicetitan/cp-react-hooks';
import { ContentLink, LoadStatus } from '@servicetitan/cp-ui';
import { BodyText, Button, Form, Icon, Stack } from '@servicetitan/design-system';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { AuthRecoveryStore } from '../../stores/recovery.store';
import { useAuthCodeParams, useAuthHistory } from '../../utils/use-auth-history';
import { AuthLayout } from '../layout/auth.layout';
import { AuthHeadline } from '../layout/components';
import { passwordError, PasswordInput } from '../ui/password.input';

export const AuthRecoveryPasswordProvider = provide({ singletons: [AuthRecoveryStore] })(
    ({ children }) => children
);

export const AuthRecoveryPasswordComponent = observer(() => {
    const [
        {
            canSubmit,
            form,
            init,
            loading,
            passwordValidator,
            showPasswordUpdated,
            submit,
            submitStatus,
        },
    ] = useDependencies(AuthRecoveryStore);
    const { confirm, password } = form.$;

    const history = useAuthHistory();
    const { code } = useAuthCodeParams();

    useInvariableEffect(() => init(code));

    useEffect(() => {
        if (loading === LoadStatus.Error) {
            history.replace('recovery', undefined, { info: 'expired' });
        }
    }, [loading, history]);

    const isSubmitting = submitStatus === LoadStatus.Loading;

    return showPasswordUpdated ? (
        <AuthLayout>
            <Stack justifyContent="center" className="m-b-2">
                <Icon name="check_circle" className="c-green-400" size={48} />
            </Stack>

            <AuthHeadline title="Password updated" />

            <BodyText subdued className="ta-center m-t-1 m-b-4">
                Your new password has been saved.
                <br />
                Go ahead and sign in.
            </BodyText>

            <Button width="full" primary href={history.href('login')}>
                Sign In
            </Button>
        </AuthLayout>
    ) : (
        <AuthLayout loading={loading === LoadStatus.Loading}>
            <AuthHeadline title="Create New Password" />

            <Form className="m-t-2">
                <PasswordInput
                    label="Password"
                    value={password.value}
                    onChange={password.onChangeHandler}
                    error={passwordError(passwordValidator, password.error)}
                    disabled={isSubmitting}
                    autoComplete="new-password"
                />

                <PasswordInput
                    label="Confirm password"
                    value={confirm.value}
                    onChange={confirm.onChangeHandler}
                    error={confirm.error}
                    disabled={isSubmitting}
                />

                <Button
                    width="full"
                    primary
                    disabled={!canSubmit}
                    onClick={submit}
                    loading={isSubmitting}
                >
                    Update Password
                </Button>

                <BodyText className="ta-center m-t-3">
                    <ContentLink href={history.href('login')} primary>
                        Back to sign in
                    </ContentLink>
                </BodyText>
            </Form>
        </AuthLayout>
    );
});

export const AuthRecoveryPassword = () => (
    <AuthRecoveryPasswordProvider>
        <AuthRecoveryPasswordComponent />
    </AuthRecoveryPasswordProvider>
);
